/* eslint-disable import/no-unresolved */
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ConfirmPasswordRequestEmailComponent } from 'assets/components';
import Countdown from '../../../utilities/counter';
import { verifyEmailScopes } from '../../../utilities/constants';
import {
  EMAIL_TOTP_CHECK,
  EMAIL_VERIFY_BY_CODE,
  PHONE_VERIFICATION_SEND,
  REQUEST_RESET_PASSWORD,
} from '../mutations';
import { showAlert } from '../../../utilities/notifications';
import { FLOWS, sendStats } from '../../../services/mixpanel';
import {
  MXP_RESET_PASSWORD_CONFIRM_EMAIL_SUCCESS,
  MXP_RESET_PASSWORD_CONFIRM_EMAIL_VISIT,
  MXP_RESET_PASSWORD_PHONE_CONFIRM_REQUEST,
  MXP_RESET_PASSWORD_PHONE_CONFIRM_VISIT,
  MXP_RESET_PASSWORD_REQUEST,
  MXP_RESET_PASSWORD_RESEND,
} from '../mixpanel-events';
import { RECAPTCHA_EVENTS } from '../constants';
import {
  cacheRecaptchaV3failure,
  clearRecaptchaV3failure,
  resetUserSession,
} from '../../../utilities/caching';
import { OnboardingContext } from '../../../contexts/onboarding/context';
import RecaptchaModal from '../../shared/RecaptchaModal';

const emailCountdown = new Countdown();

const ConfirmPasswordRequestEmail = () => {
  const { t: translate } = useTranslation();
  const { state } = useLocation();
  const history = useHistory();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);
  const [recV2Token, setRecV2Token] = useState('');

  const [isSubmitReqeust, setIsSubmitReqeust] = useState(false);

  const { state: onboardingState } = useContext(OnboardingContext);

  const totpFeatureEnabled = onboardingState?.mainSettings?.feature_allow_totp;

  const [timer, setTimer] = useState();
  const [form, updateForm] = useState({
    email: state?.email?.toLowerCase(),
    code: '',
    codeScope: verifyEmailScopes.RESET_PASSWORD,
  });
  const [resendCount, setResendCount] = useState(0);

  const [userData, setUserDate] = useReducer((prev, next) => ({ ...prev, ...next }), {
    resetKey: '',
    user: {},
    totp_active: false,
    totp_enabled: false,
    isEmailVerified: false,
  });

  useEffect(() => {
    emailCountdown.bind(setTimer);
    emailCountdown.start();
    return () => {
      emailCountdown.stop();
    };
  }, []);

  const { mutate: checkTOTP, isLoading: isCheckingTOTPLoading } = useMutation(EMAIL_TOTP_CHECK, {
    onSettled: (data, error) => {
      if (error) {
        if (error?.status === 404) {
          setUserDate(null);
        } else {
          showAlert({ message: error.message, type: 'danger' });
        }
      } else {
        setUserDate({ totp_active: data.totp_active, totp_enabled: data.totp_enabled });
      }
    },
  });

  useEffect(() => {
    sendStats(MXP_RESET_PASSWORD_CONFIRM_EMAIL_VISIT, {}, FLOWS.PASSWORD_RESET);
  }, []);

  useEffect(() => {
    if (!state?.email) {
      history.push('/onboarding/login');
    } else {
      checkTOTP({ email: state?.email?.toLowerCase() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutate: sendPhoneVerificationCode, isLoading: isSendingVerificationCode } = useMutation(
    PHONE_VERIFICATION_SEND,
    {
      onSettled: (data, error) => {
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          resetUserSession();
          sendStats(MXP_RESET_PASSWORD_PHONE_CONFIRM_REQUEST, {}, FLOWS.PASSWORD_RESET);
          history.push(`confirm-phone-number`, {
            resetKey: userData.resetKey,
            via: 'sms',
            country_code: state.country_code || userData?.user?.country_code,
            phone_number: state.phone_number || userData?.user?.phone_number,
            resetPasswordFlow: true,
            verification_id: state?.verification_id,
          });
        }
      },
    }
  );

  const handleNextStepProcess = async () => {
    if (totpFeatureEnabled && userData?.totp_enabled && userData?.totp_active) {
      history.push(`confirm-phone-number`, {
        resetKey: userData.resetKey,
        isTOTP: true,
        email: state?.email?.toLowerCase(),
        country_code: state.country_code || userData?.user?.country_code,
        phone_number: state.phone_number || userData?.user?.phone_number,
        resetPasswordFlow: true,
        verification_id: state?.verification_id,
      });
    } else if (
      (state?.country_code || userData?.user?.country_code) &&
      (state?.phone_number || userData?.user?.phone_number)
    ) {
      sendStats(MXP_RESET_PASSWORD_PHONE_CONFIRM_VISIT, {}, FLOWS.PASSWORD_RESET);
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_VERIFY_PHONE_NUMBER);
      if (!token) {
        return false;
      }
      sendPhoneVerificationCode({
        via: 'sms',
        country_code: state.country_code || userData?.user?.country_code,
        phone_number: state.phone_number || userData?.user?.phone_number,
        verificationId: state?.verification_id,
        token,
      });
    } else {
      history.push(`change/${userData.resetKey}`, {
        ...state,
        ...userData,
      });
    }
    return true;
  };

  const { mutate: verifyEmailByCode, isLoading: isLoadingVerifyEmail } = useMutation(
    EMAIL_VERIFY_BY_CODE,
    {
      onSettled: (data, error) => {
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({
              message: translate('invalid_code_msg_email_verification'),
              type: 'danger',
            });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          const { success } = data;
          if (success) {
            sendStats(
              MXP_RESET_PASSWORD_CONFIRM_EMAIL_SUCCESS,
              { user: data.user, isEmailVerified: true },
              FLOWS.PASSWORD_RESET
            );
            setUserDate({
              resetKey: data.reset_password_key,
              user: data.user,
              isEmailVerified: true,
            });
          }
        }
      },
    }
  );

  const { mutate: requestResetPassword, isLoading: isResetPasswordRequested } = useMutation(
    REQUEST_RESET_PASSWORD,
    {
      onSettled: (_, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          emailCountdown.start();
        }
        sendStats(
          MXP_RESET_PASSWORD_REQUEST,
          { success: !error, error, resendCount },
          FLOWS.PASSWORD_RESET
        );
      },
    }
  );

  useEffect(() => {
    if (userData && userData?.isEmailVerified) {
      handleNextStepProcess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const resendResetPasswordEmail = (e) => {
    e.preventDefault();
    sendStats(MXP_RESET_PASSWORD_RESEND, {}, FLOWS.PASSWORD_RESET);
    setResendCount(resendCount + 1);
    requestResetPassword(state.email?.toLowerCase());
  };

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    updateForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitReqeust(true);

    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.RESET_PASSWORD_VERIFY_EMAIL);
      if (!token) {
        return false;
      }

      verifyEmailByCode({ ...form, token, verificationId: state?.verification_id });
    } catch (error) {
      return false;
    }

    return true;
  };

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="email-confirmation"
          loading={
            isLoadingVerifyEmail || isSendingVerificationCode || isCheckingTOTPLoading
              ? 'true'
              : 'false'
          }
          disabled={
            form.code === '' ||
            isLoadingVerifyEmail ||
            isSendingVerificationCode ||
            isCheckingTOTPLoading
          }>
          {translate('confirm_button_label')}
        </button>
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="email-confirmation"
          loading={
            isLoadingVerifyEmail || isSendingVerificationCode || isCheckingTOTPLoading
              ? 'true'
              : 'false'
          }
          disabled={
            form.code === '' ||
            isLoadingVerifyEmail ||
            isSendingVerificationCode ||
            isCheckingTOTPLoading
          }>
          {translate('confirm_button_label')}
        </button>
      </div>
    </>
  );

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    if (isSubmitReqeust) {
      setIsSubmitReqeust(false);
      verifyEmailByCode({ ...form, token: tokenV2, verificationId: state?.verification_id });
    } else if (
      (state?.country_code || userData?.user?.country_code) &&
      (state?.phone_number || userData?.user?.phone_number)
    ) {
      sendPhoneVerificationCode({
        via: 'sms',
        country_code: state.country_code || userData?.user?.country_code,
        phone_number: state.phone_number || userData?.user?.phone_number,
        verificationId: state?.verification_id,
        token: tokenV2,
      });
    }
  };

  return (
    <>
      <ConfirmPasswordRequestEmailComponent
        form={form}
        timer={timer}
        renderButtons={renderButtons}
        resendResetPasswordEmail={resendResetPasswordEmail}
        isResetPasswordRequested={isResetPasswordRequested}
        handleSubmit={handleSubmit}
        handleChange={handleChange}
      />
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isLoadingVerifyEmail || isSendingVerificationCode}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default ConfirmPasswordRequestEmail;
